import '../stylesheets/main.scss';

import htmx from 'htmx.org';

import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';

import Alpine from 'alpinejs';
import intersect from '@alpinejs/intersect';

// Alpine.js stores
// See https://alpinejs.dev/globals/alpine-store
import NavigationStore from './stores/navigation';
import ReducedMotionStore from './stores/reduced-motion';

// Alpine.js directives
// See https://alpinejs.dev/advanced/extending#custom-directives
import MediaQueryDirective from './directives/media-query';
import ResizeObserverDirective from './directives/resizeobserver';

// Alpine.js components
// See https://alpinejs.dev/globals/alpine-data#registering-from-a-bundle
import PasswordComponent from '../components/password/javascripts';
import ScrollContainerComponent from '../components/scroll-container/javascripts';
import FileUploadComponent from '../components/file-upload/javascripts';
import VideoComponent from '../components/video/javascripts';
import DialogComponent from '../components/dialog/javascripts';
import FormComponent from '../components/form/javascripts';
import AccordionComponent from '../components/accordion/javascripts';
import AudioFragmentComponent from '../components/audio-fragment/javascripts';
import AsideLogic from '../components/split-art-practice/javascripts/asideLogic';
import ExhibitionTitleComponent from '../components/exhibition-title/javascripts';
import VideoFragmentComponent from '../components/video-fragment/javascripts';
import TabsComponent from '../components/tabs/javascripts';
import NavigationPopupComponent from '../components/navigation-popup/javascripts';
import SearchBoxComponent from '../components/search-box/javascripts';
import SearchResultsComponent from '../components/search-results/javascripts';
import ListItemComponent from '../components/list-item/javascripts';
import DateChooserComponent from '../components/date-chooser/javascripts';
import FragmentsComponent from '../components/fragments/javascripts';
import FormLayerComponent from '../components/form-layer/javascripts';
import EventFragmentComponent from '../components/event-fragment/javascripts';
import NotificationComponent from '../components/notification/javascripts';

import SplitScrollTemplate from '../templates/split-scroll/javascripts/split-scroll';

// htmx initialization
// See https://htmx.org/docs/#webpack
window.htmx = htmx;

// Alpine.js initialization
// See https://alpinejs.dev/essentials/installation#as-a-module
window.Alpine = Alpine;

Alpine.plugin(intersect);

Alpine.store('navigation', NavigationStore);
Alpine.store('reducedMotion', ReducedMotionStore);

Alpine.directive('mq', MediaQueryDirective);
Alpine.directive('resizeobserver', ResizeObserverDirective);

Alpine.data('Password', PasswordComponent);
Alpine.data('ScrollContainer', ScrollContainerComponent);
Alpine.data('FileUpload', FileUploadComponent);
Alpine.data('Video', VideoComponent);
Alpine.data('Dialog', DialogComponent);
Alpine.data('Form', FormComponent);
Alpine.data('Accordion', AccordionComponent);
Alpine.data('AudioFragment', AudioFragmentComponent);
Alpine.data('FormLayer', FormLayerComponent);
Alpine.data('AsideLogic', AsideLogic);
Alpine.data('ExhibitionTitle', ExhibitionTitleComponent);
Alpine.data('VideoFragment', VideoFragmentComponent);
Alpine.data('Tabs', TabsComponent);
Alpine.data('NavigationPopup', NavigationPopupComponent);
Alpine.data('SearchBox', SearchBoxComponent);
Alpine.data('SearchResults', SearchResultsComponent);
Alpine.data('ListItem', ListItemComponent);
Alpine.data('DateChooser', DateChooserComponent);
Alpine.data('Fragments', FragmentsComponent);
Alpine.data('EventFragment', EventFragmentComponent);
Alpine.data('Notification', NotificationComponent);

Alpine.data('SplitScroll', SplitScrollTemplate);

Alpine.start();

// All links with target="_blank" attribute should have an attribute rel="noopener". This has performance and security reasons https://developers.google.com/web/tools/lighthouse/audits/noopener
document.querySelectorAll("[target='_blank']").forEach((link) => {
  link.setAttribute('rel', 'noopener');
});

// Disable body scroll when the navigation popup is open
Alpine.effect(() => {
  const isOpen = Alpine.store('navigation').isOpen;
  document.body.style.overflow = isOpen ? 'hidden' : 'auto';
});

const lightbox = new PhotoSwipeLightbox({
  gallery: 'main',
  children: 'a[data-pswp]',
  pswpModule: () => import('photoswipe')
});

lightbox.init();

/* Update dynamic formie tokens like the CSRF token to circumvent
 * static Blitz caching.
 *
 * See https://verbb.io/craft-plugins/formie/docs/template-guides/cached-forms
 */
const $forms = document.querySelectorAll('.fui-form');

$forms.forEach(async ($form) => {
  const $csrfInput = $form.querySelector('input[name="CRAFT_CSRF_TOKEN"]');
  const handle = $form.querySelector('input[name="handle"]')?.value;

  if (!handle || !$csrfInput) {
    return;
  }

  const res = await fetch(
    `/actions/formie/forms/refresh-tokens?form=${handle}`
  );

  const json = await res.json();

  // CSRF token
  $csrfInput.value = json.csrf.token;

  // Find the JavaScript captcha hidden input, so we can update it
  if (json.captchas && json.captchas.javascript) {
    const jsCaptcha = json.captchas.javascript;

    const $input = $form.querySelector(`input[name="${jsCaptcha.sessionKey}"]`);
    if ($input) {
      $input.value = jsCaptcha.value;
    }
  }

  // Find the Duplicate captcha hidden input, so we can update it
  if (json.captchas && json.captchas.duplicate) {
    const duplicateCaptcha = json.captchas.duplicate;

    const $input = $form.querySelector(
      `input[name="${duplicateCaptcha.sessionKey}"]`
    );
    if ($input) {
      $input.value = duplicateCaptcha.value;
    }
  }

  if ($form.form && $form.form.formTheme) {
    $form.form.formTheme.updateFormHash();
  }
});

const REDUCE_MOTION_KEY = 'prefers-reduced-motion';

const initReduceMotion = () => {
  // Sync the prefers-reduced-motion browser setting into the Alpine store
  const mq = window.matchMedia('(prefers-reduced-motion)');
  mq.addEventListener('change', (e) => {
    Alpine.store('reducedMotion').reduceMotion = e.matches;
  });

  // Sync the localstorage reduce-motion setting into the Alpine store
  // localStorage only overrides the browser setting if it is set to
  // reduce motion and the browser setting is not.
  const storedValue = window.localStorage.getItem(REDUCE_MOTION_KEY);
  const initialValue = storedValue === null ? mq.matches : storedValue === '1';
  Alpine.store('reducedMotion').reduceMotion = initialValue;

  // Continuously sync the current user setting into localStorage
  Alpine.effect(() => {
    const reduceMotion = Alpine.store('reducedMotion').reduceMotion;

    if (reduceMotion) {
      window.localStorage.setItem(REDUCE_MOTION_KEY, '1');
    } else {
      window.localStorage.removeItem(REDUCE_MOTION_KEY);
    }

    document.body.classList.toggle('reduce-motion', reduceMotion);
  });
};

initReduceMotion();
